/**
 * Filter-tab placement helpers for the /process ATLAS form.
 *
 * A field's tab is derived from its current value, so clearing an input would
 * move the field out of the active tab on the very keystroke that empties it —
 * mid-edit. These helpers let the form pin the field being edited to the tab it
 * is rendered in and announce the move only once the user leaves the field.
 */
import { resolveFieldTab } from './fieldProvenance';
import type { FieldTabInput } from './fieldProvenance';

/** Keys of the filter tabs shown above the form area. */
export type FilterTabKey = 'extracted' | 'ai_prefilled' | 'errors' | 'optional';

export interface FilterTabInput extends FieldTabInput {
  /** field currently carries a gRPC/API validation error */
  hasError: boolean;
}

/** i18n keys for the tab labels, used when telling the user where a field went. */
export const FILTER_TAB_LABEL_KEYS: Record<FilterTabKey, string> = {
  extracted: 'atlasFields.filterTabs.extracted',
  ai_prefilled: 'atlasFields.filterTabs.aiPrefilled',
  errors: 'atlasFields.filterTabs.errors',
  optional: 'atlasFields.filterTabs.otherFields',
};

/**
 * Which filter tab a field belongs to right now. Mirrors the per-tab filters in
 * AtlasStructuredForm: an error puts the field in the Errors tab, otherwise the
 * provenance resolver decides ('none' is the internal key for "Other Fields").
 */
export function resolveFilterTab(input: FilterTabInput): FilterTabKey {
  if (input.hasError) return 'errors';
  const tab = resolveFieldTab(input);
  return tab === 'none' ? 'optional' : tab;
}

/**
 * Merge the fields pinned while being edited back into a tab's live field list,
 * keeping the section's canonical field order. Returns the live list unchanged
 * when nothing is pinned or every pinned field is already visible.
 */
export function mergeStickyFields<T extends { path: string }>(
  liveFields: T[],
  sectionFields: T[],
  stickyPaths: Set<string>,
): T[] {
  if (stickyPaths.size === 0) return liveFields;
  const visiblePaths = new Set(liveFields.map(f => f.path));
  const pinnedButHidden = sectionFields.filter(f => stickyPaths.has(f.path) && !visiblePaths.has(f.path));
  if (pinnedButHidden.length === 0) return liveFields;
  pinnedButHidden.forEach(f => visiblePaths.add(f.path));
  return sectionFields.filter(f => visiblePaths.has(f.path));
}

/** Field path owning the element that received focus/blur, or null. */
export function fieldPathFromEventTarget(target: EventTarget | null): string | null {
  if (!(target instanceof HTMLElement)) return null;
  const owner = target.closest('[data-field-path]');
  return owner ? owner.getAttribute('data-field-path') : null;
}
